import React from 'react';

import { flip, offset, shift } from '@floating-ui/react';
import cx from 'classnames';
import { EditorState, Modifier, SelectionState } from 'draft-js';
import { HexColorPicker } from 'react-colorful';

import inputS from 'components/shared/FormAdapters/FormDefault/Input/index.module.css';
import { inlineStylesMap } from 'components/shared/Wysiwyg/customStyles';
import { useWysiwygContext } from 'components/shared/Wysiwyg/Wysiwyg.contex';

import { BaseItemButtonWithPortal, PortalControl } from '../Base';
import s from './Color.module.css';
import { getCurrentColor } from './utils';

export const Color = () => {
  const { props, editor } = useWysiwygContext();
  const portalRef = React.useRef<PortalControl>(null);
  const selectionState = React.useRef<SelectionState | null>(null);
  const [localColor, setLocalColor] = React.useState(() => getCurrentColor(editor.editorState));

  const openPortal = () => {
    selectionState.current = editor.editorState.getSelection();
    if (!selectionState.current?.isCollapsed()) {
      editor.setEditorState(inlineStylesMap.selected.toggle(editor.editorState, 'yes'));
      portalRef.current?.open();
    }
  };

  const removeSelectStyle = () => {
    if (selectionState.current) {
      editor.setEditorState(
        EditorState.forceSelection(inlineStylesMap.selected.toggle(editor.editorState, 'yes'), selectionState.current),
      );
    }
  };

  const onSubmit = (color: string) => {
    const selection = editor.editorState.getSelection();
    const content = Modifier.removeInlineStyle(
      editor.editorState.getCurrentContent(),
      selection,
      'CUSTOM_SELECTED_yes',
    );
    const newEditorState = EditorState.push(editor.editorState, content, 'change-inline-style');
    editor.handleChange(inlineStylesMap.color.add(newEditorState, color), true);

    portalRef.current?.close();
    editor.focus();
  };

  const currentColor = getCurrentColor(editor.editorState);

  return (
    <BaseItemButtonWithPortal
      ref={portalRef}
      portalRef={editor.refs.portalRef}
      portalContent={<Portal localColor={localColor} setLocalColor={setLocalColor} onSubmit={onSubmit} />}
      onMouseDown={openPortal}
      onBlur={() => {
        removeSelectStyle();
        onSubmit(localColor);
      }}
      floatingOptions={{ middleware: [offset({ mainAxis: -100 }), flip(), shift()] }}
    >
      <div className={cx(s.btn, props.type === 'tertiary' && s.sticky)} style={{ backgroundColor: currentColor }} />
    </BaseItemButtonWithPortal>
  );
};

type PortalProps = {
  localColor: string;
  setLocalColor: (color: string) => void;
  onSubmit: (color: string) => void;
};

const Portal = ({ localColor, setLocalColor, onSubmit }: PortalProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value.replace(/[^0-9a-fA-F]/g, '').substring(0, 6);
    setLocalColor(`#${newColor}`);
  };

  return (
    <div className={s.wrapper}>
      <HexColorPicker color={localColor} onChange={setLocalColor} />
      <input
        autoFocus
        color={localColor}
        onChange={handleChange}
        value={localColor}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit(localColor);
          }
        }}
        className={cx(inputS.input, s.input)}
      />
    </div>
  );
};
